/**
 * "Download Excel" for a Performance page's baseline pickers.
 *
 * Every straight-line Performance page derives the export the same way — the report's own chart
 * channels as columns, the report's own x-axis as the row key — so the derivation lives here rather
 * than being pasted into each of the four pages. A page supplies its `xAxis` signal, its
 * `chartGroups` memo and its baseline slots; it gets back the button's state and click handler.
 *
 * See `utils/baselineExcel.ts` for what the sheet actually contains.
 */

import { createMemo, createSignal, type Accessor } from 'solid-js';
import { toastStore } from '../store/toastStore';
import { exportBaselineExcel, type BaselineExcelSeries } from '../utils/baselineExcel';
import type { useBaselineDisplay } from './useBaselineDisplay';

/** One baseline slot, as `useBaselineDisplay` returns it. */
export type BaselineDisplaySlot = ReturnType<typeof useBaselineDisplay>;

export interface UseBaselineExcelExportOptions {
  /** The page's x-axis channel, e.g. `Tws_kph` or `Bsp_kph`. */
  getXAxis: Accessor<string>;
  /** The page's `chartGroups()` memo — read for the y channels, post Bsp-on-X swap. */
  getChartGroups: Accessor<any[]>;
}

export function useBaselineExcelExport(options: UseBaselineExcelExportOptions) {
  /**
   * The channels the report plots, in chart order — the columns of the sheet.
   *
   * Read off `chartGroups()` rather than the raw chart config so the "Bsp on X moves TWS to Y" swap
   * is already applied and the columns match what is on screen. The x-axis channel is dropped
   * because it is the row key, and the aggregate type is ignored: the export writes AVG curves
   * only, so an STD series contributes its channel once, not a second column.
   */
  const channels = createMemo<string[]>(() => {
    const xChannel = options.getXAxis();
    const seen = new Set<string>();
    const list: string[] = [];
    for (const group of options.getChartGroups()) {
      for (const series of (group?.charts?.[0]?.series || [])) {
        const field = series?.yaxis?.dataField || series?.yaxis?.name || '';
        if (!field || field === xChannel || seen.has(field)) { continue; }
        seen.add(field);
        list.push(field);
      }
    }
    return list;
  });

  /** Which slot's picker is building a download, so only its button says "Exporting…". */
  const [exportingSlot, setExportingSlot] = createSignal<number | null>(null);

  /**
   * Write the picker's baseline to a .xlsx download.
   *
   * Exports the slot's *pending* selection rather than what it is currently drawing — the button
   * sits next to Select, so it has to export the baseline and config the user is looking at. Only
   * this slot: see `utils/baselineExcel.ts` for why a second baseline does not belong in the sheet.
   */
  const handleExport = async (slot: BaselineDisplaySlot): Promise<void> => {
    const pendingName = slot.availableBaselines()
      .find((b) => String(b.baseline_id) === slot.selectedKey())?.name;
    const baseline: BaselineExcelSeries = {
      name: pendingName || slot.baselineName() || `Baseline ${slot.slot}`,
      config: slot.modalConfig(),
      fit: slot.modalFit(),
    };

    setExportingSlot(slot.slot);
    try {
      const result = await exportBaselineExcel({
        baseline,
        xChannel: options.getXAxis(),
        channels: channels(),
      });
      if (!result.success) {
        toastStore.showToast('error', 'Export failed', result.message || 'Could not export the baseline.');
      }
    } finally {
      setExportingSlot(null);
    }
  };

  return { channels, exportingSlot, handleExport };
}
